/**
 * Manager untuk Direct USB Printing via WebUSB API (Kabel USB OTG).
 * Berjalan secara native di browser & Webview Android tanpa butuh aplikasi pihak ketiga seperti RawBT (Bebas Watermark!).
 */

export const isWebUSBSupported = () => {
  return typeof navigator !== 'undefined' && !!navigator.usb;
};

let activeUsbDevice = null;
let activeEndpointNumber = null;

/**
 * Mencari dan meminta izin perangkat USB Thermal Printer
 */
export const requestUsbPrinter = async () => {
  if (!isWebUSBSupported()) {
    throw new Error("Browser / Perangkat Anda belum mendukung WebUSB API. Pastikan menggunakan Chrome / Webview Android modern.");
  }

  try {
    // Minta user memilih printer USB yang terhubung via OTG
    const device = await navigator.usb.requestDevice({
      filters: [] // Menampilkan semua perangkat USB yang terhubung
    });

    await openAndClaimUsbDevice(device);

    const savedInfo = {
      vendorId: device.vendorId,
      productId: device.productId,
      productName: device.productName || 'Printer Thermal USB',
      manufacturerName: device.manufacturerName || 'Generic USB Printer',
      endpointNumber: activeEndpointNumber,
    };

    localStorage.setItem('savedUsbPrinter', JSON.stringify(savedInfo));
    return savedInfo;
  } catch (err) {
    console.error("Gagal memilih printer USB:", err);
    throw err;
  }
};

/**
 * Membuka koneksi dan mengklaim interface USB
 */

const openAndClaimUsbDevice = async (device) => {
  if (!device.opened) {
    await device.open();
  }

  if (device.configuration === null) {
    await device.selectConfiguration(1);
  }

  // Cari interface printer (biasanya interface 0)
  const interfaces = device.configuration.interfaces;
  let targetInterfaceNumber = 0;
  let targetAlternateSetting = 0;
  let targetEndpointNumber = null;

  for (const iface of interfaces) {
    for (const alt of iface.alternates) {
      // Cari endpoint OUT pada alternate setting yang benar.
      // Sebagian printer USB tidak memakai alternate setting 0 sebagai jalur data.
      const outEndpoint = alt.endpoints.find((ep) => ep.direction === 'out');
      if (outEndpoint) {
        targetInterfaceNumber = iface.interfaceNumber;
        targetAlternateSetting = alt.alternateSetting;
        targetEndpointNumber = outEndpoint.endpointNumber;
        break;
      }
    }
    if (targetEndpointNumber !== null) break;
  }

  if (targetEndpointNumber === null) {
    // Fallback default endpoint number OUT jika auto-detect tidak ketemu
    targetEndpointNumber = 1;
  }

  try {
    await device.claimInterface(targetInterfaceNumber);
    // Endpoint hanya valid setelah alternate interface yang memilikinya aktif.
    if (typeof device.selectAlternateInterface === 'function') {
      await device.selectAlternateInterface(targetInterfaceNumber, targetAlternateSetting);
    }
  } catch (e) {
    console.warn("Claim atau pemilihan alternate interface gagal:", e);
    const message = String(e?.message || "");
    if (/protected class|access denied|denied/i.test(message)) {
      throw new Error("Printer sedang dikunci driver Windows atau driver POS 80 mm. WebUSB tidak dapat mengambil alih perangkat ini. Gunakan port COM, tutup driver POS, atau gunakan helper desktop raw ESC POS.");
    }
    throw new Error(`Interface printer USB tidak dapat diaktifkan. ${message || "Periksa driver atau izin perangkat."}`);
  }

  activeUsbDevice = device;
  activeEndpointNumber = targetEndpointNumber;
};

/**
 * Mengirim raw bytes ArrayBuffer (ESC/POS) langsung ke printer USB
 */
export const sendRawUsbBytes = async (arrayBuffer) => {
  if (!isWebUSBSupported()) {
    throw new Error("WebUSB tidak didukung pada browser ini.");
  }

  let device = activeUsbDevice;

  // Jika belum terkoneksi di memori, coba hubungkan kembali berdasarkan savedUsbPrinter
  if (!device) {
    const saved = localStorage.getItem('savedUsbPrinter');
    if (!saved) {
      throw new Error("Belum ada printer USB yang dihubungkan.");
    }
    const { vendorId, productId } = JSON.parse(saved);
    const pairedDevices = await navigator.usb.getDevices();
    device = pairedDevices.find(d => d.vendorId === vendorId && d.productId === productId);

    if (!device) {
      throw new Error("Printer USB tersimpan tidak terdeteksi. Hubungkan kabel USB OTG lalu klik sambungkan.");
    }

    await openAndClaimUsbDevice(device);
  }

  if (!device.opened) {
    await device.open();
  }

  const endpoint = activeEndpointNumber || 1;
  const dataView = new Uint8Array(arrayBuffer);

  // Kirim data secara bertahap (chunk size 64 bytes atau 512 bytes)
  const chunkSize = 512;
  for (let i = 0; i < dataView.length; i += chunkSize) {
    const chunk = dataView.subarray(i, i + chunkSize);
    await device.transferOut(endpoint, chunk);
  }

  return { method: 'usb', success: true };
};

/**
 * Cek status printer USB tersimpan
 */
export const getSavedUsbPrinter = () => {
  const saved = localStorage.getItem('savedUsbPrinter');
  if (!saved) return null;
  try {
    return JSON.parse(saved);
  } catch (e) {
    return null;
  }
};

/**
 * Hapus printer USB tersimpan
 */
export const disconnectUsbPrinter = async () => {
  if (activeUsbDevice) {
    try {
      await activeUsbDevice.close();
    } catch (e) {}
    activeUsbDevice = null;
    activeEndpointNumber = null;
  }
  localStorage.removeItem('savedUsbPrinter');
};
